import {
  ApplicantPortalService,
  StageProgress,
  UnprocessableFields,
} from 'api-clients/monolith';
import React from 'react';

import { TimeZone } from 'features/ContextsProvider/useTimeZone';

export type StageData = Awaited<
  ReturnType<
    typeof ApplicantPortalService.getInternalApiPortalApplicationsStagesNew
  >
>;
export type StageOrTranslationData =
  | StageData
  | (StageData &
      Awaited<
        ReturnType<
          typeof ApplicantPortalService.postInternalApiPortalFunnelsTranslationStages
        >
      >[]);

export type StageProgressData = Awaited<
  ReturnType<
    typeof ApplicantPortalService.getInternalApiPortalApplicationsStageProgress
  >
>;

type MergedCurrentStage = StageProgress &
  Awaited<
    ReturnType<
      typeof ApplicantPortalService.postInternalApiPortalFunnelsTranslationStages
    >
  >[0];
export type StageProgressOrTranslationData =
  | StageProgressData
  | (StageProgressData & {
      // eslint-disable-next-line camelcase
      current_stage: MergedCurrentStage;
    });

export type RefreshOptions = {
  // A poll keeps the stage on screen when one reload fails, so its next tick
  // can retry. Without it a single failed request replaces the stage with the
  // error page for good.
  quiet?: boolean;
};

export interface StageContextData {
  stage: StageOrTranslationData;
  progress: StageProgressOrTranslationData;
  onSubmit: (formData: FormData, errorMessage?: React.ReactNode) => void;
  onContinue?: () => void;
  timeZone?: TimeZone;
  setTimeZone?: (arg0: TimeZone) => void;
  updateApplicantTimeZone?: (timezone: string) => Promise<void>;
  refreshStageData: (options?: RefreshOptions) => void;
  serverValidationErrors?: UnprocessableFields;
}

export const defaultContext: StageContextData = {
  stage: {
    id: 123,
    external_id: 'abc',
    type: 'CustomStage',
    title: 'Custom Stage',
    portal_body: '<p>Hello</p>',
    advance_url: '/apply/test_account/applications/123/stages/456/advance',
    show_i9_compliance_center_agent: false,
  },
  progress: {
    applicant_name: 'Dwayne Johnson',
    should_show_stages: true,
    current_stage: {
      title: 'Custom Stage',
      position: 2,
    },
  },
  timeZone: 'America/Los_Angeles',
  onSubmit: () => undefined,
  setTimeZone: () => undefined,
  updateApplicantTimeZone: async () => {
    await new Promise(resolve => resolve(undefined));
  },
  refreshStageData: () => undefined,
};

export const StageContext =
  React.createContext<StageContextData>(defaultContext);
